import React from "react";
import styled from "styled-components";
import { Box } from "@origin-digital/ods-core";
import { IconChevronRightSmall, IconSearch } from "@origin-digital/ods-icons";
import { ISubLinkDto } from "../../contract";
import {
  QuickAction,
  CategoryArticleItem,
} from "../../services/categories/types";
import { isIconUrl, isValidHref } from "../QuickActions/helpers";
import { PopularArticles } from "../PopularArticles/PopularArticles";

const SectionHeading = styled.p`
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.neutralDark};
`;

const IconPlaceholder = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  flex-shrink: 0;
`;

const IconImage = styled.span<{ $src: string }>`
  display: inline-block;
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  mask-image: url(${({ $src }) => $src});
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
  -webkit-mask-image: url(${({ $src }) => $src});
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;
  background-color: ${({ theme }) => theme.colors.neutral};
`;

const resolveIconSlot = (icon: string | null | undefined): React.ReactNode => {
  if (!icon || !isIconUrl(icon)) {
    return <IconPlaceholder aria-hidden="true" />;
  }
  return <IconImage $src={icon} aria-hidden="true" />;
};

const FaqPillButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xsmall}px;
  padding: 4px 12px;
  border: 1px solid ${({ theme }) => theme.colors.disabled};
  border-radius: ${({ theme }) => theme.shape.borderRadii.large}px;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0;
  color: ${({ theme }) => theme.colors.neutral};
  &:hover {
    background: ${({ theme }) => theme.palette.action.hover};
  }
`;

const PillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.small}px;
  margin-top: ${({ theme }) => theme.space.small}px;
`;

const QuickActionPill = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xsmall}px;
  padding: 4px 12px;
  border: 1px solid ${({ theme }) => theme.colors.disabled};
  border-radius: ${({ theme }) => theme.shape.borderRadii.large}px;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.neutral};
  text-decoration: none;
  &:hover {
    background: ${({ theme }) => theme.palette.action.hover};
  }
`;

export interface SearchEmptyStateProps {
  faqs: string[];
  quickActions: QuickAction[];
  popularArticles: CategoryArticleItem[];
  onFaqSelect: (question: string) => void;
}

const toSubLink = (article: CategoryArticleItem): ISubLinkDto => ({
  label: article.title,
  href: article.url,
  excerpt: article.excerpt,
});

export const SearchEmptyState: React.FC<SearchEmptyStateProps> = ({
  faqs,
  quickActions,
  popularArticles,
  onFaqSelect,
}) => {
  return (
    <Box
      data-id="search-empty-state"
      paddingY="medium"
      display="flex"
      flexDirection="column"
      gap="xlarge"
    >
      {faqs.length > 0 && (
        <Box>
          <SectionHeading>Try asking...</SectionHeading>
          <PillsContainer>
            {faqs.map((faq) => (
              <FaqPillButton
                key={faq}
                onClick={() => onFaqSelect(faq)}
                data-id="faq-pill"
              >
                <IconSearch size="xxxsmall" color="neutral" />
                {faq}
              </FaqPillButton>
            ))}
          </PillsContainer>
        </Box>
      )}

      {quickActions.length > 0 && (
        <Box>
          <SectionHeading>Quick actions</SectionHeading>
          <PillsContainer>
            {quickActions
              .filter((action) => action.label && isValidHref(action.href))
              .map((action) => (
                <QuickActionPill
                  key={action.href || action.label}
                  href={action.href!} // safe: isValidHref filter guarantees href is defined
                  data-id={`quick-action-pill-${action.label
                    ?.toLowerCase()
                    .replace(/\s+/g, "-")}`}
                >
                  {resolveIconSlot(action.icon)}
                  {action.label}
                  <IconChevronRightSmall
                    variant="solid"
                    size="xxsmall"
                    color="neutral"
                  />
                </QuickActionPill>
              ))}
          </PillsContainer>
        </Box>
      )}

      {popularArticles.length > 0 && (
        <Box>
          <SectionHeading>Popular articles</SectionHeading>
          <Box>
            <PopularArticles popularArticles={popularArticles.map(toSubLink)} />
          </Box>
        </Box>
      )}
    </Box>
  );
};
